import { Box } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ActionDrawer, { UI, btnSx } from "../components/ActionDrawer";
import AddTechnicianModal from "../components/AddTechnicianModal";
import DashTable, { Notice, PageHead, Tag } from "../components/DashTable";
import PageLoader from "../components/Loader";
import { useToast } from "../components/Toast";
import apiService from "../Services/api.service";

const shortDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
};

/* OL3 assign, OL4 reassign. Roster plus the orders waiting on one. */
const Technicians = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const token = localStorage.getItem("token");

  const [technicians, setTechnicians] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [opsLeadSummary, setOpsLeadSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [active, setActive] = useState(null);
  const [addTechnicianOpen, setAddTechnicianOpen] = useState(false);

  const fetchRows = async () => {
    setIsLoading(true);
    setLoadFailed(false);
    try {
      const [techRes, summaryRes] = await Promise.all([
        apiService.fetchTechnicians(token),
        apiService.fetchOpsLeadSummary(token),
      ]);
      setTechnicians(techRes.technicians || []);
      setUnassigned(techRes.unassigned || []);
      setOpsLeadSummary(summaryRes.data || null);
    } catch (error) {
      setLoadFailed(true);
      toast.error("Error while fetching technicians");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemoveTechnician = async (technician) => {
    if (!window.confirm(`Remove ${technician.name} from the roster? Past jobs stay on record, but nothing new can be assigned to them.`)) {
      return;
    }
    try {
      await apiService.setTechnicianActive(technician.id, false, token);
      toast.success(`${technician.name} removed from the roster`);
      fetchRows();
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to remove technician");
    }
  };

  const toItem = (row) => ({
    id: row.declinedBy ? "OL4" : "OL3",
    screenKey: "assign-technician",
    orderId: row.orderId,
    orderNumber: row.orderNumber,
    clinicName: row.clinicName,
    context: {
      city: row.city,
      state: row.state,
      address: row.address,
      contact: row.contact,
      doctorName: row.doctorName,
      doctorContact: row.doctorContact,
      activationCode: row.activationCode,
      mapsLink: row.mapsLink,
      paymentDue: row.paymentDue,
      siteNotes: row.siteNotes,
      declinedBy: row.declinedBy,
      technicians,
      trackingId: row.trackingId || null,
      trackingUrl: row.trackingUrl || null,
      expectedDelivery: row.expectedDelivery
        ? new Date(row.expectedDelivery).toLocaleDateString("en-IN")
        : null,
    },
  });

  /* Shared across every Ops Lead page — not recomputed per page. */
  const kpis = opsLeadSummary
    ? [
        { label: "Needs your action", value: opsLeadSummary.needsAction },
        { label: "Blocking", value: opsLeadSummary.blocking, alarm: true },
        { label: "Jobs in flight", value: opsLeadSummary.jobsInFlight },
        { label: "Unassigned", value: opsLeadSummary.unassigned },
      ]
    : [];

  return (
    <Box sx={{ padding: { xs: "18px 16px 50px", md: "24px 28px 60px" }, maxWidth: 1240 }}>
      <PageHead
        title="Technicians"
        lede="Assignment considers base city and current open jobs. Accept time is the response lag you are choosing against."
        kpis={kpis}
      />

      <Notice>
        <b>Open decision.</b> Response SLA and auto-reassignment are not in the
        flow. Assigning to a slow responder stalls the order invisibly, and a
        decline has no defined reassignment path.
      </Notice>

      {unassigned.length > 0 && (
        <DashTable
          columns={[
            { label: "Clinic" },
            { label: "City" },
            { label: "State" },
            { label: "Shipped" },
            { label: "Expected delivery" },
            { label: "Status" },
            { label: "Action" },
          ]}
          rows={unassigned.map((row) => ({
            id: row.orderId,
            cells: [
              row.clinicName,
              row.city,
              row.state,
              shortDate(row.dispatchedAt),
              shortDate(row.expectedDelivery),
              row.declinedBy ? (
                <Tag tone="b">Declined by {row.declinedBy}</Tag>
              ) : (
                <Tag tone="s">Unassigned</Tag>
              ),
              <Box
                component="button"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setActive(toItem(row));
                }}
                sx={btnSx("ghost")}
              >
                {row.declinedBy ? "Reassign" : "Assign"}
              </Box>,
            ],
          }))}
          onRowClick={(id) => navigate(`/dashboard/orders/${id}`)}
          foot={`${unassigned.length} job${
            unassigned.length === 1 ? "" : "s"
          } waiting on a technician`}
        />
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
        <Box
          component="button"
          type="button"
          onClick={() => setAddTechnicianOpen(true)}
          sx={btnSx("ghost")}
        >
          Add technician
        </Box>
      </Box>

      <DashTable
        columns={[
          { label: "Technician" },
          { label: "Base" },
          { label: "Open jobs", num: true },
          { label: "Next free" },
          { label: "Installs last 30d", num: true },
          { label: "Avg accept time" },
          { label: "Current job" },
          { label: "" },
        ]}
        rows={technicians.map((t) => ({
          key: t.id,
          cells: [
            <Box component="span" sx={{ fontWeight: 500 }}>
              {t.name}
            </Box>,
            t.base,
            t.openJobs ?? 0,
            shortDate(t.nextFree),
            t.installs30d ?? 0,
            t.avgAcceptMinutes != null && t.avgAcceptMinutes > 120 ? (
              <Box component="span" sx={{ color: UI.red, fontWeight: 500 }}>
                {Math.round(t.avgAcceptMinutes / 60)}h
              </Box>
            ) : t.avgAcceptMinutes != null ? (
              `${t.avgAcceptMinutes} min`
            ) : (
              "—"
            ),
            t.currentOrderId ? (
              <Box
                component="a"
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  navigate(`/dashboard/orders/${t.currentOrderId}`);
                }}
                sx={{ color: UI.p, textDecoration: "none" }}
              >
                {t.currentClinicName}
              </Box>
            ) : (
              "—"
            ),
            <Box
              component="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveTechnician(t);
              }}
              sx={btnSx("ghost")}
            >
              Remove
            </Box>,
          ],
        }))}
        emptyText="No technicians on the roster."
        errorText={loadFailed ? "Could not load technicians." : null}
        onRetry={fetchRows}
        foot={`${technicians.length} technicians · ${technicians.reduce(
          (sum, t) => sum + (t.openJobs || 0),
          0,
        )} open jobs`}
      />

      <ActionDrawer
        open={Boolean(active)}
        screenKey={active?.screenKey}
        item={active}
        onClose={() => setActive(null)}
        onSaved={() => {
          setActive(null);
          fetchRows();
        }}
      />

      <AddTechnicianModal
        open={addTechnicianOpen}
        onClose={() => setAddTechnicianOpen(false)}
        onCreated={() => {
          setAddTechnicianOpen(false);
          fetchRows();
        }}
      />

      {isLoading && <PageLoader />}
    </Box>
  );
};

export default Technicians;
